import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Phone, Quote, Users } from "lucide-react";
import { scrollToId } from "@/components/Reveal";

const ETHOS_QUOTE_URL = "https://agents.ethoslife.com/invite/c31bd";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Values", href: "#values", testid: "nav-link-values" },
  { label: "Founder", href: "#founder", testid: "nav-link-founder" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled
          ? "border-gold/20 bg-[#0F091A]/85 backdrop-blur-xl backdrop-saturate-150"
          : "border-transparent bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          onClick={() => scrollToId("#top")}
          className="group flex items-center gap-3 text-left"
          data-testid="nav-brand"
        >
          <span className="flex h-9 w-9 items-center justify-center border border-gold/40 bg-royal/60 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-obsidian">
            <Crown size={16} strokeWidth={1.5} />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-sm font-semibold tracking-[0.22em] text-white">
              VISION ARIES
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
              Financial Impact
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollToId(l.href)}
              data-testid={l.testid}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:text-gold"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => scrollToId("#join-team")}
            data-testid="nav-join-team-tab"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:text-gold"
          >
            Join the Team
          </button>
          <a
            href={ETHOS_QUOTE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-quote-tab"
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-gold/90 transition-colors duration-300 hover:text-gold-light"
          >
            <Quote size={12} /> Get a Quote
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+17709121516"
            data-testid="nav-phone-link"
            className="hidden items-center gap-2 font-mono text-xs tracking-widest text-white/70 transition-colors hover:text-gold xl:flex"
          >
            <Phone size={13} /> (770) 912-1516
          </a>
          <button
            onClick={() => scrollToId("#contact")}
            data-testid="nav-request-services-tab"
            className="flex items-center gap-2 border border-gold/50 bg-gold/10 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian"
          >
            <Users size={13} /> Request Services
          </button>
        </div>
      </div>
    </motion.header>
  );
};
